import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Toolbar from "@mui/material/Toolbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { makeStyles } from "tss-react/mui";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Paper from "@mui/material/Paper";

import TableWithStickHeader from "../Common/Table/TableWithStickHeader";
import { ButtonUI, ConfirmPopup, KMKDrawer } from "../Common";
import { showToast } from "../../store/actions";
import WorkspaceClass from "../../service/workspace";
import { setPrevState } from "../../util/common";

const useStyles = makeStyles()({
  shareWSTable: {
    "& .MuiTableCell-root:last-child": {
      padding: "6px 16px !important",
    },
    "&:last-child td": {
      border: 0,
      padding: "6px 16px !important",
    },
    "&:last-child th": {
      padding: "6px 16px !important",
    },
  },
});

const columns = [
  { label: "Name", width: 200, align: "left" },
  { label: "Role", width: 100, align: "left" },
  { label: "Action", width: 50, align: "center" },
];

const ConfirmDeleteUser = ({ confirmMessage, deleteCancel, removeUser, loadingForConfirm }) => (
  <ConfirmPopup
    configData={{
      popupTitle: "Remove User",
      popupMessage: confirmMessage,
      handleCancel: deleteCancel,
      handleClick: removeUser,
      loadingForConfirm,
      maxWidth: "600px",
      minHeight: "130px",
    }}
  />
);

export default function ShareWorkspace({ setOpen }) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { Workspace_id, Name } = useSelector((state) => state?.workspace?.workspace);
  const loggedInUser = useSelector((state) => state.user);

  const [shareWSState, setShareWSState] = useState({
    loading: false,
    totalUserList: [],
    selectedOptions: [],
    role: "",
    roleList: [],
  });

  const [deleteUserState, setDeleteUserState] = useState({
    deleteUser: {},
    openConfirm: false,
    confirmMessage: "",
    loadingForConfirm: false,
  });

  const tableData = useMemo(
    () =>
      shareWSState.totalUserList
        .filter((user) => user.isShared)
        .map((user) => [
          { value: user.label, align: "left", id: user.id, status: user.status },
          { value: user.role, align: "left" },
          {
            value: "",
            deleteIcon: loggedInUser.currentUser.User_id !== user.id ? <DeleteOutlineOutlinedIcon /> : "",
            align: "center",
          },
        ]),
    [loggedInUser.currentUser.User_id, shareWSState.totalUserList]
  );

  const onSave = async () => {
    setPrevState(setShareWSState, { loading: true });

    const submitData = {
      userList: shareWSState.selectedOptions.map((row) => row.id),
      workspaceUserRole: shareWSState.role,
    };

    const workspace = new WorkspaceClass();
    const result = await workspace.shareWorkspace(Workspace_id, submitData);

    if (result.success) {
      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
      setOpen(false);
    }
    setPrevState(setShareWSState, { loading: false });
  };

  const deleteCancel = () => {
    setPrevState(setDeleteUserState, { openConfirm: false });
    setPrevState(setDeleteUserState, {
      deleteUser: {},
      openConfirm: false,
      confirmMessage: "",
      loadingForConfirm: false,
    });
  };

  const handleDelete = (rowNo) => {
    const { id, value: username } = tableData[rowNo][0];
    const { value: role } = tableData[rowNo][1];
    setPrevState(setDeleteUserState, {
      deleteUser: { id, role, rowNo },
      openConfirm: true,
      confirmMessage: `Are you sure you want to remove "${username} (${role})" from "${Name}" Workspace?"`,
    });
  };

  const deleteCurrentRow = (rowNo) => {
    const temptotalUserList = shareWSState.totalUserList.map((user) =>
      user.id === tableData[rowNo][0].id ? { ...user, isShared: 0, role: "" } : { ...user }
    );

    setPrevState(setShareWSState, {
      totalUserList: temptotalUserList,
    });
  };

  const removeUser = async () => {
    setPrevState(setDeleteUserState, { loadingForConfirm: true });

    const submitData = {
      removeUserId: deleteUserState.deleteUser.id,
      workspaceUserRole: deleteUserState.deleteUser.role,
    };

    const workspace = new WorkspaceClass();
    const result = await workspace.removeUser(Workspace_id, submitData);

    if (result.success) {
      deleteCurrentRow(deleteUserState.deleteUser.rowNo);
      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
    }

    setPrevState(setDeleteUserState, { openConfirm: false, loadingForConfirm: false });
  };

  const handleCancel = () => setOpen(false);

  const loadUserListToShare = useCallback(async () => {
    const workspace = new WorkspaceClass();
    const result = await workspace.getUserListToShare(Workspace_id);

    if (result && result.length) {
      let userArr = [];

      result.forEach((user) => {
        userArr.push({
          id: user.User_id,
          value: user.User_id,
          label: `${user.FirstName} ${user.LastName}`,
          isShared: user.isShared,
          role: user.Permission,
          UserName: user.UserName,
          status: user.Status,
        });
      });
      setPrevState(setShareWSState, {
        totalUserList: userArr,
      });
    }
  }, [Workspace_id]);

  const getUserRoles = useCallback(async () => {
    const workspace = new WorkspaceClass();
    const result = await workspace.getUserRoles(Workspace_id);

    if (result.success) {
      let roleArr = [];

      if (result.data.userRoles && result.data.userRoles.length)
        result.data.userRoles.forEach((userRole) => {
          roleArr.push({ value: userRole.value, text: userRole.text });
        });
      setPrevState(setShareWSState, { roleList: roleArr });
    }
  }, [Workspace_id]);

  useEffect(() => {
    (async () => {
      setPrevState(setShareWSState, { loading: true });
      getUserRoles();
      await loadUserListToShare();
      setPrevState(setShareWSState, { loading: false });
    })();
  }, [Workspace_id, getUserRoles, loadUserListToShare]);

  const usersToFilter = useMemo(
    () => shareWSState.totalUserList.filter((user) => loggedInUser.currentUser.User_id !== user.id && user.status),
    [loggedInUser.currentUser.User_id, shareWSState.totalUserList]
  );

  return (
    <KMKDrawer anchor="right" open={true} width={250}>
      <Toolbar />
      <Paper
        elevation={0}
        sx={{
          position: "sticky",
          right: 12,
          top: 70,
          borderRadius: "0px",
          backgrounColor: "black",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          alignItems: "baseline",
        }}
      >
        <Typography
          sx={{
            flexGrow: 1,
            paddingLeft: "18px",
          }}
          color="primary"
        >
          Share Workspace
        </Typography>

        <IconButton aria-label="close" onClick={handleCancel}>
          <CloseIcon />
        </IconButton>
      </Paper>
      <Box sx={{ width: { xs: "100%", sm: "500px" }, height: "100%", padding: "25px 20px" }} role="presentation">
        <Backdrop
          sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={shareWSState.loading}
        >
          <CircularProgress color="primary" />
        </Backdrop>

        <Grid container spacing={2}>
          <Grid size={12}>
            <Autocomplete
              data-id="shareUserName"
              multiple
              fullWidth
              limitTags={10}
              id="checkboxes-tags-demo"
              options={usersToFilter}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.value}>
                    {`${option.label} (${option.UserName})${option.role ? " - " + option.role : ""}`}
                  </li>
                );
              }}
              renderInput={(params) => <TextField variant="outlined" {...params} label="Name" placeholder="Name" />}
              onChange={(e, selectedObj) => {
                if (selectedObj.length) {
                  selectedObj[0].isShared = 1;
                  setPrevState(setShareWSState, {
                    selectedOptions: [...selectedObj],
                  });
                } else
                  setPrevState(setShareWSState, {
                    selectedOptions: [],
                  });
              }}
              ListboxProps={{
                style: {
                  maxHeight: "150px",
                },
              }}
            />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth variant="outlined" sx={{ my: 2 }}>
              <InputLabel>Role *</InputLabel>
              <Select
                data-id="shareRole"
                required
                value={shareWSState.role}
                onChange={(e) => setPrevState(setShareWSState, { role: e.target.value })}
                label="Role"
                MenuProps={{ sx: { height: "220px" } }}
              >
                {shareWSState.roleList.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <ButtonUI
              data-id="saveBtn"
              variant="contained"
              color="success"
              style={{ width: "auto" }}
              onClick={onSave}
              disabled={!(shareWSState.selectedOptions.length && shareWSState.role)}
            >
              Share
            </ButtonUI>
            <ButtonUI
              data-id="cancelBtn"
              variant="contained"
              color="secondary"
              style={{
                marginLeft: 15,
                width: "auto",
                backgroundColor: "#3a3d4a",
              }}
              onClick={handleCancel}
            >
              Cancel
            </ButtonUI>
          </Grid>
          <Grid className={classes.shareWSTable} size={12}>
            <TableWithStickHeader
              {...{
                columns: columns,
                tableData,
                textMaxLength: 50,
                tableMaxHeight: "calc(100vh - 480px)",
                handleDeleteClick: handleDelete,
                noDataColspan: 3,
                noDataMessage: "",
              }}
            />
          </Grid>
        </Grid>
        {deleteUserState.openConfirm && (
          <ConfirmDeleteUser
            confirmMessage={deleteUserState.confirmMessage}
            deleteCancel={deleteCancel}
            removeUser={removeUser}
            loadingForConfirm={deleteUserState.loadingForConfirm}
          />
        )}
      </Box>
    </KMKDrawer>
  );
}
